import React, { useState, useRef, useEffect } from "react";
import { Map, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.locatecontrol/dist/L.Control.Locate.min.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";

import "leaflet-control-geocoder";
import "leaflet.locatecontrol";

import InputField from "../InputField";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.6.0/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.6.0/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.6.0/images/marker-shadow.png",
});

export default function MapAddressField({
  center = [-6.2088, 106.8456],
  zoom = 13,
  height = "300px",
  onLocationSelect = () => undefined,
  initialLocation = null,
  initialAddress = "",
  initialZoom = null,
}) {
  // State for clicked location
  const [clickedLocation, setClickedLocation] = useState(initialLocation);
  const [clickedAddress, setClickedAddress] = useState(initialAddress);
  const [isLocationFromGPS, setIsLocationFromGPS] = useState(false);
  const [mapCenter, setMapCenter] = useState(
    initialLocation ? [initialLocation.lat, initialLocation.lng] : center
  );
  const [mapZoom, setMapZoom] = useState(initialZoom || zoom);

  const mapRef = useRef();
  const locateControlRef = useRef(null);
  const geocoderControlRef = useRef(null); // NEW: Reference to geocoder control
  const isInitializedRef = useRef(false); // NEW: Track if map is already initialized

  // Initialize component with props values
  useEffect(() => {
    if (initialLocation) {
      setClickedLocation(initialLocation);
      setMapCenter([initialLocation.lat, initialLocation.lng]);
      setIsLocationFromGPS(false); // Initial location is not from GPS

      if (!initialAddress) {
        reverseGeocode(initialLocation.lat, initialLocation.lng);
      }
    }

    if (initialAddress) {
      setClickedAddress(initialAddress);
    }
  }, [initialLocation, initialAddress]);

  // Update states when props change
  useEffect(() => {
    if (initialLocation) {
      setClickedLocation(initialLocation);
      setMapCenter([initialLocation.lat, initialLocation.lng]);
      setIsLocationFromGPS(false);
    }
  }, [initialLocation]);

  useEffect(() => {
    if (initialAddress !== undefined) {
      setClickedAddress(initialAddress);
    }
  }, [initialAddress]);

  // Function to stop GPS tracking
  const stopGPSTracking = () => {
    if (mapRef.current && mapRef.current.leafletElement) {
      const leafletMap = mapRef.current.leafletElement;
      leafletMap.stopLocate(); // Stop GPS tracking
    }

    if (locateControlRef.current) {
      try {
        locateControlRef.current.stop(); // Stop locate control
      } catch (error) {
        console.warn("Error stopping locate control:", error);
      }
    }
  };

  // Reverse geocoding to get address from coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      const data = await response.json();

      if (data && data.display_name) {
        setClickedAddress(data.display_name);
        return data.display_name;
      } else {
        setClickedAddress(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
        return `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      const fallbackAddress = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
      setClickedAddress(fallbackAddress);
      return fallbackAddress;
    } finally {
      console.log("Reverse geocoding completed");
    }
  };

  // Handle map click
  const handleMapClick = async (e) => {
    const { lat, lng } = e.latlng;

    // STOP GPS tracking when user manually clicks on map
    stopGPSTracking();

    // Update state immediately
    setClickedLocation({ lat, lng });
    setIsLocationFromGPS(false); // This is a manual selection

    // Get address from coordinates
    const address = await reverseGeocode(lat, lng);

    // Call parent callback if provided
    if (onLocationSelect) {
      onLocationSelect({
        lat,
        lng,
        address,
      });
    }
  };

  // Handle address change
  const handleAddressChange = (e) => {
    const newAddress = e.target.value;
    setClickedAddress(newAddress);

    // Call parent callback with updated address
    if (onLocationSelect && clickedLocation) {
      onLocationSelect({
        lat: clickedLocation.lat,
        lng: clickedLocation.lng,
        address: newAddress,
      });
    }
  };

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializedRef.current) {
      console.log("Map already initialized, skipping...");
      return;
    }

    // Add a small delay to ensure map is fully initialized
    const initTimer = setTimeout(() => {
      const map = mapRef.current;

      if (!map || !map.leafletElement) {
        console.warn("Map not ready, retrying...");
        return;
      }

      const leafletMap = map.leafletElement;

      // Check if map is properly initialized
      if (!leafletMap._loaded) {
        console.warn("Map not loaded yet, waiting...");
        return;
      }

      try {
        // Mark as initialized to prevent multiple runs
        isInitializedRef.current = true;

        // Add click event listener
        leafletMap.on("click", handleMapClick);

        // Add geocoder search control (only once)
        const geocoder = L.Control.Geocoder.nominatim({
          geocodingQueryParams: {
            countrycodes: "id",
            limit: 5,
          },
        });

        const control = L.Control.geocoder({
          geocoder: geocoder,
          defaultMarkGeocode: false,
          placeholder: "Cari lokasi...",
          errorMessage: "Lokasi tidak ditemukan",
          collapsed: false,
          suggestMinLength: 3,
          suggestTimeout: 250,
          showResultIcons: true,
        })
          .on("markgeocode", function (e) {
            const latlng = e.geocode.center;
            const lat = latlng.lat;
            const lng = latlng.lng;
            const address = e.geocode.name;

            // STOP GPS tracking when using search
            stopGPSTracking();

            // Update state when using search
            setClickedLocation({ lat, lng });
            setClickedAddress(address);
            setIsLocationFromGPS(false);

            leafletMap.setView(latlng, 16);
            setMapCenter([lat, lng]);
            setMapZoom(16);

            if (onLocationSelect) {
              onLocationSelect({
                lat,
                lng,
                address,
              });
            }
          })
          .addTo(leafletMap);

        // Store reference to geocoder control
        geocoderControlRef.current = control;

        // Wait a bit more before adding locate control
        setTimeout(() => {
          try {
            // Add locate control (current location) with safer options
            const locateControl = L.control
              .locate({
                position: "topright",
                strings: {
                  title: "Lokasi Saya",
                },
                locateOptions: {
                  maxZoom: 16,
                  enableHighAccuracy: true,
                  timeout: 15000,
                  maximumAge: 300000,
                  watch: false,
                },
                drawCircle: false,
                drawMarker: false,
                showCompass: false,
                flyTo: false,
                setView: false,
                icon: "fas fa-home",
                iconLoading: "fa fa-spinner fa-spin",
                iconElementTag: "i",
                onLocationError: function (err) {
                  setIsLocationFromGPS(false);
                  alert("Tidak dapat menemukan lokasi Anda: " + err.message);
                },
              })
              .addTo(leafletMap);

            // Store reference to locate control
            locateControlRef.current = locateControl;

            console.log("All controls added successfully");
          } catch (error) {
            console.error("Error adding locate control:", error);
          }
        }, 200);

        // Handle locate events
        leafletMap.on("locationfound", function (e) {
          console.log("GPS location found:", e.latlng);

          const lat = e.latlng.lat;
          const lng = e.latlng.lng;

          // Always update location when GPS finds it
          setClickedLocation({ lat, lng });
          setMapCenter([lat, lng]);
          setMapZoom(16);
          setIsLocationFromGPS(true);

          // Manually set view since we disabled flyTo
          try {
            leafletMap.setView([lat, lng], 16);
          } catch (error) {
            console.warn("Error setting view:", error);
          }

          // Get address for current location
          reverseGeocode(lat, lng);

          if (onLocationSelect) {
            onLocationSelect({
              lat,
              lng,
              address: "Lokasi Anda saat ini",
            });
          }

          // Stop locate after getting location
          setTimeout(() => {
            try {
              if (locateControlRef.current) {
                locateControlRef.current.stop();
              }
            } catch (error) {
              console.warn("Error stopping locate control:", error);
            }
          }, 100);
        });

        leafletMap.on("locationerror", function (e) {
          console.error("GPS location error:", e);
          setIsLocationFromGPS(false);
          alert(
            "Tidak dapat menemukan lokasi Anda. Pastikan GPS dan izin lokasi diaktifkan."
          );
        });

        leafletMap.on("locateactivate", function () {
          console.log("GPS activated - searching for location...");
        });

        leafletMap.on("locatedeactivate", function () {
          console.log("GPS deactivated");
        });
      } catch (error) {
        console.error("Error initializing map controls:", error);
        // Reset initialization flag on error
        isInitializedRef.current = false;
      }
    }, 300);

    // Cleanup function
    return () => {
      clearTimeout(initTimer);

      console.log("Cleaning up map controls");

      // Reset initialization flag
      isInitializedRef.current = false;

      // eslint-disable-next-line
      const map = mapRef.current;
      if (map && map.leafletElement) {
        const leafletMap = map.leafletElement;

        try {
          // Stop all location tracking
          if (leafletMap.stopLocate) {
            leafletMap.stopLocate();
          }

          // Remove event listeners
          leafletMap.off("click", handleMapClick);
          leafletMap.off("locationfound");
          leafletMap.off("locationerror");
          leafletMap.off("locateactivate");
          leafletMap.off("locatedeactivate");

          // Remove controls if they exist
          if (geocoderControlRef.current) {
            leafletMap.removeControl(geocoderControlRef.current);
            geocoderControlRef.current = null;
          }

          if (locateControlRef.current) {
            leafletMap.removeControl(locateControlRef.current);
            locateControlRef.current = null;
          }
        } catch (error) {
          console.warn("Error during cleanup:", error);
        }
      }
    };

    // eslint-disable-next-line
  }, []); // Empty dependency array to run only once

  return (
    <div className="d-flex flex-column gap-4">
      <div style={{ height, width: "100%" }}>
        <Map
          ref={mapRef}
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {clickedLocation && (
            <Marker position={[clickedLocation.lat, clickedLocation.lng]}>
              <Popup>
                <div>
                  <strong>
                    {isLocationFromGPS ? "Lokasi GPS" : "Lokasi Terpilih"}
                  </strong>
                  <br />
                  <small>Lat: {clickedLocation.lat.toFixed(6)}</small>
                  <br />
                  <small>Lng: {clickedLocation.lng.toFixed(6)}</small>
                  <br />
                  <small>{clickedAddress || "Alamat tidak tersedia"}</small>
                </div>
              </Popup>
            </Marker>
          )}
        </Map>
      </div>

      {/* Address Input */}
      <InputField
        type="textarea"
        placeholder="Alamat akan tampil di sini ketika Anda mengklik peta..."
        value={clickedAddress}
        onChange={handleAddressChange}
        label="Alamat Lengkap"
      />
    </div>
  );
}
